import React, {
  useCallback,
  useEffect,
  useRef,
} from 'react';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import Skeleton from 'core/components/Skeleton';
import TextArea from 'core/components/TextArea';
import {
  PCCEligibleIcdCodeDTO,
} from 'dtos';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  PCCEncounterValue,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import RTMEncounterSelection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMEncounterSelection';
import {
  RTM_MONITORING_LABEL,
  useRTMPlanTemplate,
  useRTMSettings,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/hooks';
import {
  normalizeContent,
} from 'views/EMR/Settings/helper';
import {
  useGetPCCStatusByPatientId,
} from 'views/EMR/hooks/pcc';

type Props = {
  patientId: number;
  visitNoteId?: number;
  planText: string;
  onPlanTextChange: (value: string) => void;
  onCanApproveChange: (canApprove: boolean) => void;
  isEncounterSelectionEditable?: boolean;
  encounterValue: PCCEncounterValue | null;
  onEncounterValueChange: (value: PCCEncounterValue | null) => void;
};

function formatDiagnosisLabel({ code, description }: PCCEligibleIcdCodeDTO) {
  let label = '';
  if (notEmptyString(code) && notEmptyString(description)) {
    label = `${code} — ${description}`;
  } else if (notEmptyString(code)) {
    label = code ?? '';
  } else if (notEmptyString(description)) {
    label = description ?? '';
  }
  return label;
}

function isEncounterValueValid(
  isEncounterSelectionEditable: boolean,
  encounterValue: PCCEncounterValue | null,
): boolean {
  const hasValidEncounter = (encounterValue?.encounterId ?? 0) > 0;
  const hasValidAppointment = (encounterValue?.newAppointment?.userId ?? 0) > 0
    && (encounterValue?.newAppointment?.serviceFacilityId ?? 0) > 0;

  return !isEncounterSelectionEditable
    || hasValidEncounter
    || hasValidAppointment;
}

export default function RTMRecommendation({
  patientId,
  visitNoteId,
  planText,
  onPlanTextChange,
  onCanApproveChange,
  isEncounterSelectionEditable = false,
  encounterValue,
  onEncounterValueChange,
}: Props) {
  const hasSeededPlanRef = useRef(notEmptyString(planText));
  const { data: settings, isLoading: isLoadingSettings } = useRTMSettings(true);
  const templateName = settings?.rtmPlanTemplateName ?? '';
  const shouldFetchPlan = notEmptyString(templateName);

  const {
    data: planTemplateContent,
    isLoading: isLoadingPlan,
    isError: isPlanError,
  } = useRTMPlanTemplate(templateName, shouldFetchPlan);

  const {
    data: pccStatus,
    isLoading: isLoadingPCCStatus,
  } = useGetPCCStatusByPatientId(patientId);

  const eligibleIcdCodes = pccStatus?.rtm?.eligibleIcdCodes ?? [];

  const isLoading = isLoadingSettings
    || (shouldFetchPlan && isLoadingPlan)
    || isLoadingPCCStatus;
  const diagnosisLabels = compact(eligibleIcdCodes.map(formatDiagnosisLabel));
  const isPlanValid = !isLoading
    && !isEmpty(templateName)
    && !isPlanError
    && !isEmpty(planText);
  const canApprove = isPlanValid
    && isEncounterValueValid(isEncounterSelectionEditable, encounterValue);

  useEffect(() => {
    if (hasSeededPlanRef.current || notEmptyString(planText)) {
      hasSeededPlanRef.current = true;
      return;
    }
    if (notEmptyString(planTemplateContent)) {
      hasSeededPlanRef.current = true;
      onPlanTextChange(normalizeContent(planTemplateContent?.toString() ?? ''));
    }
  }, [onPlanTextChange, planTemplateContent, planText]);

  useEffect(() => {
    onCanApproveChange(canApprove);
  }, [canApprove, onCanApproveChange]);

  const handlePlanChange = useCallback((value: string) => {
    onPlanTextChange(value);
  }, [onPlanTextChange]);

  return (
    <div className="flex flex-col gap-form-row text-xs flex-1">
      <div className="flex-1 flex flex-col gap-form-row">
        <Wrapper id="rtm-diagnosis" label="Diagnosis">
          {isLoadingPCCStatus && (
            <Skeleton
              className="!h-10 !w-full !rounded"
              variant="rectangular"
            />
          )}
          {!isLoadingPCCStatus && isEmpty(diagnosisLabels) && (
            <p className="text-gray-500">No diagnosis codes available.</p>
          )}
          {!isLoadingPCCStatus && !isEmpty(diagnosisLabels) && (
            <ul className="list-disc pl-5 space-y-1">
              {diagnosisLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </Wrapper>

        <RTMEncounterSelection
          patientId={patientId}
          visitNoteId={visitNoteId}
          value={encounterValue}
          disabled={!isEncounterSelectionEditable}
          onChange={onEncounterValueChange}
        />

        <Wrapper id="rtm-monitoring" label="Monitoring">
          <p>{RTM_MONITORING_LABEL}</p>
        </Wrapper>

        <Wrapper id="rtm-treatment-plan" label="Treatment plan (will be appended to the existing plan)">
          {isLoading && (
            <Skeleton
              className="!h-[120px] !w-full !rounded"
              variant="rectangular"
            />
          )}
          {!isLoading && isEmpty(templateName) && (
            <p>No RTM plan template configured.</p>
          )}
          {!isLoading && !isEmpty(templateName) && isPlanError && (
            <p>Unable to load RTM plan template.</p>
          )}
          {!isLoading && !isEmpty(templateName) && !isPlanError && (
            <TextArea
              value={planText}
              onChange={handlePlanChange}
              trackingLabel="RTM Treatment Plan"
              id={VisitNoteSOAPSectionNames.PlanTemplate}
              actions={['dictation', 'field']}
            />
          )}
        </Wrapper>
      </div>
    </div>
  );
}
